import { useState } from 'react'
import { CheckCircle2, Loader2, ThumbsDown, ThumbsUp, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CHARTER_SECTION_OPTIONS } from '../../utils/charterSections'

interface RatificationVotePanelProps {
  totalMembers: number
  yesCount: number
  votes: Record<string, { vote: boolean; voted_at: string }>
  currentMemberId: string
  memberNames: Record<string, string>
  onVote: (vote: boolean, concern?: string, sectionKey?: string) => Promise<void>
}

export function RatificationVotePanel({
  totalMembers,
  yesCount,
  votes,
  currentMemberId,
  memberNames,
  onVote,
}: RatificationVotePanelProps) {
  const [concern, setConcern] = useState('')
  const [sectionKey, setSectionKey] = useState('')
  const [isVoting, setIsVoting] = useState(false)
  const [showConcernField, setShowConcernField] = useState(false)

  const myVote = votes[currentMemberId]
  const hasVoted = Boolean(myVote)
  const progressPct = totalMembers > 0 ? Math.round((yesCount / totalMembers) * 100) : 0

  const handleVote = async (vote: boolean) => {
    if (hasVoted) return
    if (!vote && !concern.trim()) {
      setShowConcernField(true)
      return
    }
    setIsVoting(true)
    try {
      await onVote(
        vote,
        concern.trim() || undefined,
        vote ? undefined : sectionKey || undefined
      )
      setShowConcernField(false)
      setConcern('')
      setSectionKey('')
    } finally {
      setIsVoting(false)
    }
  }

  return (
    <Card className='border-indigo-200 bg-indigo-50'>
      <CardHeader className='pb-2'>
        <CardTitle className='text-sm font-semibold text-indigo-900'>
          🗳️ Charter Ratification Vote
        </CardTitle>
        <p className='text-xs text-indigo-700'>
          All members must vote to ratify the charter.
        </p>
      </CardHeader>
      <CardContent className='space-y-4'>
        {/* Progress */}
        <div className='space-y-1.5'>
          <div className='flex justify-between text-xs text-indigo-700'>
            <span>Votes cast</span>
            <span>
              {Object.keys(votes).length} / {totalMembers}
            </span>
          </div>
          <Progress value={progressPct} className='h-2 [&>div]:bg-indigo-500' />
          <p className='text-xs text-indigo-600'>
            {yesCount} yes · {Object.values(votes).filter((v) => !v.vote).length} no
          </p>
        </div>

        <Separator className='bg-indigo-200' />

        {/* Per-member status */}
        <div className='space-y-1.5'>
          {Object.entries(memberNames).map(([memberId, name]) => {
            const vote = votes[memberId]
            return (
              <div key={memberId} className='flex items-center justify-between text-xs'>
                <span className='text-indigo-800 font-medium'>
                  {name}
                  {memberId === currentMemberId && ' (you)'}
                </span>
                {vote ? (
                  vote.vote ? (
                    <CheckCircle2 className='h-4 w-4 text-emerald-500' />
                  ) : (
                    <XCircle className='h-4 w-4 text-red-500' />
                  )
                ) : (
                  <span className='text-gray-400 italic'>Pending…</span>
                )}
              </div>
            )
          })}
        </div>

        {/* My vote UI */}
        {!hasVoted ? (
          <div className='space-y-3'>
            {showConcernField && (
              <div className='space-y-2'>
                <div className='space-y-1.5'>
                  <Label htmlFor='ratification-concern-section' className='text-xs text-indigo-700'>
                    Which section? (optional)
                  </Label>
                  <select
                    id='ratification-concern-section'
                    value={sectionKey}
                    onChange={(e) => setSectionKey(e.target.value)}
                    className='w-full rounded-md border border-input bg-white px-2 py-1.5 text-xs'
                  >
                    <option value=''>Not sure — let the moderator work it out</option>
                    {CHARTER_SECTION_OPTIONS.map(({ key, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='space-y-1.5'>
                  <Label className='text-xs text-indigo-700'>
                    Describe your concern (required for No vote)
                  </Label>
                  <Textarea
                    value={concern}
                    onChange={(e) => setConcern(e.target.value)}
                    placeholder='What needs to change before you can ratify?'
                    rows={2}
                    className='text-xs resize-none bg-white'
                  />
                </div>
              </div>
            )}
            <div className='flex gap-2'>
              <Button
                onClick={() => handleVote(true)}
                disabled={isVoting}
                size='sm'
                className='flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5'
              >
                {isVoting ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <ThumbsUp className='h-3.5 w-3.5' />
                )}
                Ratify
              </Button>
              <Button
                onClick={() => handleVote(false)}
                disabled={isVoting}
                size='sm'
                variant='outline'
                className='flex-1 border-red-300 text-red-600 hover:bg-red-50 gap-1.5'
              >
                <ThumbsDown className='h-3.5 w-3.5' />
                Raise Concern
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-2 text-sm'>
            {myVote.vote ? (
              <>
                <CheckCircle2 className='h-4 w-4 text-emerald-500' />
                <span className='text-emerald-700 font-medium'>You voted to ratify.</span>
              </>
            ) : (
              <>
                <XCircle className='h-4 w-4 text-red-500' />
                <span className='text-red-600 font-medium'>You raised a concern.</span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
